<template>
  <div class="cell"
    style="position: relative"
    :inert="isSaving">
    <div v-if="isSaving"
      class="quote-incident-notes-overlay" />
    <article class="box">
      <label class="label">Incident Date</label>
      <div class="field has-addons">
        <v-date-picker v-model="selectedIncidentNotesDate"
          :is-dark="$theme.isDark"
          :masks="formats"
          :attributes="attrs"
          :locale="$userInfo.locale">
          <template v-slot="{ inputValue, inputEvents }">
            <div class="field">
              <div class="control has-icons-left">
                <input type="text"
                  class="input"
                  placeholder="Incident Date"
                  :value="inputValue"
                  v-on="inputEvents"
                  readonly>
                <span class="icon is-small is-left">
                  <i class="mdi mdi-calendar mdi-18px" />
                </span>
              </div>
            </div>
          </template>
        </v-date-picker>
      </div>
      <div class="field">
        <label class="label">Notes</label>
        <div class="control">
          <textarea class="textarea"
            v-model="incidentNotesText"
            placeholder=""
            @focus="captureNotesSnapshot"
            @blur="handleNotesBlur" />
        </div>
      </div>
    </article>
  </div>
</template>

<script>
export default {
  name: 'QuoteIncidentNotes',
  props: {
    value: { type: Object, default: null },
    isSaving: { type: Boolean, default: false }
  },
  data() {
    return {
      incidentNotesText: '',
      selectedIncidentNotesDate: null,
      notesSnapshot: null,
      dateSnapshot: null,
      isSyncing: false,
      formats: { input: ['L', 'YYYY-MM-DD', 'YYYY/MM/DD'] },
      attrs: [
        {
          key: 'today',
          highlight: { backgroundColor: '#ff8080' },
          popover: { label: 'Today' },
          dates: new Date()
        }
      ]
    }
  },
  watch: {
    value(newVal) {
      this.syncFromValue(newVal)
    },
    incidentNotesText(val) {
      if (this.isSyncing) return // skip prop-driven syncs; only user input emits
      this.$emit('text-change', val)
    },
    selectedIncidentNotesDate(val) {
      if (this.isSyncing) return // skip prop-driven syncs; only user input emits
      this.$emit('date-change', val)
      const iso = val ? val.toISOString() : null
      if (iso !== this.dateSnapshot) {
        this.$emit('date-save')
      }
    }
  },
  created() {
    this.syncFromValue(this.value)
  },
  methods: {
    syncFromValue(val) {
      // Mirror the prop into local state without emitting; cleared next tick.
      this.isSyncing = true
      this.incidentNotesText = val?.remarks || ''
      const newDate = val?.remarkDateTime ? new Date(val.remarkDateTime) : null
      // Snapshot before the reactive property so date-save stays accurate.
      this.dateSnapshot = newDate ? newDate.toISOString() : null
      this.selectedIncidentNotesDate = newDate
      this.$nextTick(() => {
        this.isSyncing = false
      })
    },
    captureNotesSnapshot() {
      this.notesSnapshot = this.incidentNotesText
    },
    handleNotesBlur() {
      if (this.incidentNotesText !== this.notesSnapshot) {
        this.$emit('notes-blur')
      }
    },
    confirmDateSaved() {
      this.dateSnapshot = this.selectedIncidentNotesDate ? this.selectedIncidentNotesDate.toISOString() : null
    }
  }
}
</script>

<style lang="scss">
.quote-incident-notes-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: all;
  overflow: hidden;
  border-radius: var(--bulma-radius-small, 4px);
  background: color-mix(in srgb, var(--bulma-scheme-main) 40%, transparent);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--bulma-primary);
    opacity: 0.7;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, var(--bulma-primary) 14%, transparent) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: incident-notes-saving-shimmer 1.6s ease-in-out infinite;
  }
}

@keyframes incident-notes-saving-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>
